import { FacilityFlowCoreModulesPlanningWorkflowsTimeClockDailyAttendanceStatus as AttendanceStatus } from '../api/generated/models';
import { i18n } from '../i18n';
import { isoDate, parseUtcTimestamp } from './dateUtils';
import type {
  PlanningEntry,
  PlanningTask,
  AbsenceListDto,
  DailyEmployeeStatus,
  EmployeeListDto,
  TodayStatus,
  HeatCellData,
  UtilizationRow,
  DayTotals,
  EmployeeWorkStatus,
} from '../types/dashboard';

export function timeToMin(hhmm?: string | null): number {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map((v) => Number(v) || 0);
  return h * 60 + m;
}

export function minToHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.max(0, min - h * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function nowMinutes(date: Date = new Date()): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function getCurrentTask(
  tasks: PlanningTask[] | null | undefined,
  now: number,
): PlanningTask | null {
  if (!tasks || tasks.length === 0) return null;
  return (
    tasks.find((t) => {
      const start = timeToMin(t.startTime);
      const end = timeToMin(t.endTime);
      return end > start && start <= now && end > now;
    }) ?? null
  );
}

export function getUpcomingTasks(
  tasks: PlanningTask[] | null | undefined,
  now: number,
): PlanningTask[] {
  if (!tasks) return [];
  return tasks
    .filter((t) => timeToMin(t.startTime) > now)
    .sort((a, b) => timeToMin(a.startTime) - timeToMin(b.startTime));
}

export function getDoneTasks(
  tasks: PlanningTask[] | null | undefined,
  now: number,
): PlanningTask[] {
  if (!tasks) return [];
  return tasks
    .filter((t) => timeToMin(t.endTime) <= now)
    .sort((a, b) => timeToMin(a.startTime) - timeToMin(b.startTime));
}

export function getDayTotals(entry: PlanningEntry | null, now: number): DayTotals {
  if (!entry || !entry.tasks) {
    const target = Math.round((entry?.targetHours ?? 0) * 60);
    return { remainingMinutes: 0, targetMinutes: target };
  }
  let planned = 0;
  let done = 0;
  for (const t of entry.tasks) {
    const dur = t.durationMinutes ?? Math.max(0, timeToMin(t.endTime) - timeToMin(t.startTime));
    planned += dur;
    const end = timeToMin(t.endTime);
    const start = timeToMin(t.startTime);
    if (end <= now) {
      done += dur;
    } else if (start < now && end > now) {
      done += now - start;
    }
  }
  return {
    remainingMinutes: Math.max(0, planned - done),
    targetMinutes: Math.round((entry.targetHours ?? 0) * 60),
  };
}

export function formatHours(minutes: number): string {
  if (minutes <= 0) return '0,0h';
  const hours = minutes / 60;
  return `${hours.toFixed(1).replace('.', ',')}h`;
}

export function formatHoursDecimal(hours: number | null | undefined): string {
  if (!hours || hours <= 0) return '0,0';
  return hours.toFixed(1).replace('.', ',');
}

function mapAttendanceStatus(
  status: number | undefined,
  hasAbsenceToday: boolean,
): EmployeeWorkStatus {
  if (hasAbsenceToday) return 'absent';
  switch (status) {
    case AttendanceStatus.NUMBER_1:
      return 'in';
    case AttendanceStatus.NUMBER_4:
      return 'break';
    case AttendanceStatus.NUMBER_3:
      return 'missing';
    case AttendanceStatus.NUMBER_5:
      return 'absent';
    case AttendanceStatus.NUMBER_2:
      return 'out';
    case AttendanceStatus.NUMBER_6:
    default:
      return 'out';
  }
}

function formatUtcToHHmm(iso?: string | null): string | null {
  const date = parseUtcTimestamp(iso);
  if (!date) return null;
  const locale = i18n.language === 'en' ? 'en-US' : 'de-DE';
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function buildTodayStatus(
  employees: EmployeeListDto[],
  dailyStatuses: DailyEmployeeStatus[] | null | undefined,
  todayPlannings: PlanningEntry[],
  todayAbsences: AbsenceListDto[],
  todayIso: string,
  now: number,
): TodayStatus[] {
  const absentSet = new Set(
    todayAbsences
      .filter(
        (a) =>
          a.approvalStatus === 'Approved' &&
          a.startDate &&
          a.endDate &&
          a.startDate <= todayIso &&
          a.endDate >= todayIso,
      )
      .map((a) => a.employeeId),
  );

  const planByEmp = new Map<string, PlanningEntry>();
  for (const p of todayPlannings) {
    if (p.employeeId) planByEmp.set(p.employeeId, p);
  }

  const statusByEmp = new Map<string, DailyEmployeeStatus>();
  for (const s of dailyStatuses ?? []) {
    if (s.employeeId) statusByEmp.set(s.employeeId, s);
  }

  return employees
    .filter((e) => e.id)
    .map((emp) => {
      const id = emp.id!;
      const daily = statusByEmp.get(id);
      const plan = planByEmp.get(id);
      const status = mapAttendanceStatus(daily?.status, absentSet.has(id));

      const currentTask = plan?.tasks ? getCurrentTask(plan.tasks, now) : null;

      const since =
        status === 'in' || status === 'break'
          ? formatUtcToHHmm(daily?.clockInTime) ?? null
          : null;

      return {
        employeeId: id,
        employee: {
          firstName: emp.firstName ?? '',
          lastName: emp.lastName ?? '',
          avatarColor: null,
        },
        status,
        since,
        at: currentTask?.objectShortName ?? currentTask?.objectName ?? null,
        task: currentTask?.taskName ?? null,
        targetHours: plan?.targetHours ?? 0,
      } satisfies TodayStatus;
    });
}

export function buildUtilizationMatrix(
  employees: EmployeeListDto[],
  entries: PlanningEntry[],
  weekDays: string[],
): UtilizationRow[] {
  const byEmpDay = new Map<string, PlanningEntry>();
  for (const e of entries) {
    if (!e.employeeId || !e.date) continue;
    byEmpDay.set(`${e.employeeId}__${e.date}`, e);
  }

  return employees
    .filter((emp) => emp.id)
    .map((emp) => {
      const days: HeatCellData[] = weekDays.map((dateIso) => {
        const entry = byEmpDay.get(`${emp.id!}__${dateIso}`);
        if (!entry) {
          return { state: 'off', ratio: null, hours: 0, target: 0 };
        }
        if (entry.entryType === 'Absence' || entry.absenceTypeId) {
          return {
            state: 'absence',
            ratio: null,
            hours: 0,
            target: entry.targetHours ?? 0,
            absenceLabel: entry.absenceTypeName ?? 'Abwesenheit',
          };
        }
        const hours = entry.plannedHours ?? 0;
        const target = entry.targetHours ?? 0;
        const ratio = target > 0 ? hours / target : null;
        const conflict = (entry.tasks?.length ?? 0) > 0 && !!entry.absenceTypeId;
        return { state: 'work', ratio, hours, target, conflict };
      });
      return { employee: emp, days };
    });
}

export function aggregateAdminKpis(rows: TodayStatus[]) {
  let inCount = 0;
  let breakCount = 0;
  let absentCount = 0;
  let missingCount = 0;
  let outCount = 0;
  for (const r of rows) {
    if (r.status === 'in') inCount++;
    else if (r.status === 'break') breakCount++;
    else if (r.status === 'absent') absentCount++;
    else if (r.status === 'missing') missingCount++;
    else outCount++;
  }
  return { inCount, breakCount, absentCount, missingCount, outCount, total: rows.length };
}

export function weekUtilization(rows: UtilizationRow[]): number {
  let planned = 0;
  let target = 0;
  for (const r of rows) {
    for (const d of r.days) {
      if (d.state === 'work') {
        planned += d.hours;
        target += d.target;
      }
    }
  }
  return target > 0 ? planned / target : 0;
}

export function getMondayOfWeek(d: Date): Date {
  const result = new Date(d);
  const dow = result.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  result.setDate(result.getDate() + diff);
  result.setHours(0, 0, 0, 0);
  return result;
}

export function weekDayIsoList(monday: Date): string[] {
  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return isoDate(d);
  });
}
